// Custom React hook for PostHog
import { useEffect, useCallback, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { trackEvent, trackPageView, identifyUser, resetUser, PostHogEvents, storeEventProps } from '@/lib/posthog';
import type { UserProperties, EventProperties, StoreEventProps, TrackableStore } from '@/lib/posthog';
import type { NotificationDeliveryChannel, NotificationType } from '@shared/notifications';
import type { AuthModalTrigger } from '@shared/onboarding';
import type { BlogLinkSurface } from '@/lib/blog/tracking';

/**
 * The three places a blacklist page modal can be opened from. A UNION, not a `string`, because
 * the whole point of the prop is to keep the three apart in the breakdown — a typo'd `"Grid"`
 * would silently split one surface into two rows and nobody would notice.
 */
export type BlacklistModalSurface = 'grid' | 'spotlight' | 'suggestion';

/** Where a notification was opened from. `push` / `email` are the landing-page captures. */
export type NotificationClickSurface = 'bell_dropdown' | 'inbox_page' | 'push' | 'email' | 'whatsapp';

/** Which soft ask (or the preferences page) led to the browser push permission grant. */
export type PushPermissionSurface =
  | 'review_thank_you'
  | 'claim_submitted'
  | 'bell_first_open'
  | 'preferences'
  | 'welcome_card';

/** Where WhatsApp notification consent was granted or revoked. */
/** `onboarding` is the signup finish screen (S2); `onboarding_second_ask` is the welcome card item
 *  or the post-review ask (S1 item 3, S3). Both store consent source `onboarding`. */
export type WhatsappOptinSource =
  | 'login_otp'
  | 'claim_otp'
  | 'bell_prompt'
  | 'settings'
  | 'onboarding'
  | 'onboarding_second_ask';

/** The auth modal trigger that led to the signup, or `none` when no modal was involved. */
export type SignupTrigger = AuthModalTrigger | 'none';

/** The welcome card items a click can land on. Item 3 is WhatsApp or push, never both. */
export type WelcomeCardItem = 'review_store' | 'complete_profile' | 'whatsapp' | 'push';

export const usePostHogTracking = () => {
  const posthog = usePostHog();

  // Track page view when component mounts
  const trackPage = useCallback((pageName: string, properties: EventProperties = {}) => {
    trackPageView(pageName, properties);
  }, []);

  // Track custom event
  const track = useCallback((eventName: string, properties: EventProperties = {}) => {
    trackEvent(eventName, properties);
  }, []);

  // Identify user
  const identify = useCallback((userId: string, userProperties: UserProperties = {}) => {
    identifyUser(userId, userProperties);
  }, []);

  // Reset user on logout
  const reset = useCallback(() => {
    resetUser();
  }, []);

  // Track authentication events
  const trackAuth = useMemo(() => ({
    // Props are an allowlist on purpose (D-03): email / full_name used to be spread in here, which
    // put PII in event props. Person properties belong in identify(), never in the event.
    signUp: (method: 'email' | 'google' | 'facebook' | 'phone', props: { trigger?: SignupTrigger } = {}) => {
      track(PostHogEvents.USER_SIGNED_UP, {
        method,
        trigger: props.trigger ?? 'none',
      });
    },
    signIn: (method: 'email' | 'google' | 'facebook' | 'phone', userProperties: UserProperties = {}) => {
      track(PostHogEvents.USER_SIGNED_IN, {
        method,
        ...userProperties,
      });
    },
    signOut: () => {
      track(PostHogEvents.USER_SIGNED_OUT, {
        timestamp: new Date().toISOString(),
      });
      reset();
    },
    phoneVerified: (userProperties: EventProperties = {}) => {
      track(PostHogEvents.USER_PHONE_VERIFIED, userProperties);
    },
    // Captured centrally in AuthContext.showAuthModal — call sites only pass their trigger
    authModalShown: (trigger: string, properties: EventProperties = {}) => {
      track(PostHogEvents.AUTH_MODAL_SHOWN, { trigger, ...properties });
    },
    phoneAuthInitiated: (properties: EventProperties = {}) => {
      track(PostHogEvents.PHONE_AUTH_INITIATED, properties);
    },
    phoneOtpSent: (properties: EventProperties = {}) => {
      track(PostHogEvents.PHONE_OTP_SENT, properties);
    },
    phoneOtpResent: (properties: EventProperties = {}) => {
      track(PostHogEvents.PHONE_OTP_RESENT, properties);
    },
  }), [track, reset]);

  // Track store-related events
  const trackStore = useMemo(() => ({
    viewed: (storeId: string, storeName: string, properties: EventProperties = {}) => {
      track(PostHogEvents.STORE_VIEWED, {
        store_id: storeId,
        store_name: storeName,
        ...properties,
      });
    },
    searched: (searchTerm: string, resultsCount: number, properties: EventProperties = {}) => {
      track(PostHogEvents.STORE_SEARCHED, {
        search_term: searchTerm,
        results_count: resultsCount,
        ...properties,
      });
    },
    added: (storeId: string, storeName: string, method: 'manual' | 'scraping', properties: EventProperties = {}) => {
      track(PostHogEvents.STORE_ADDED, {
        store_id: storeId,
        store_name: storeName,
        method,
        ...properties,
      });
    },
    scraped: (url: string, platform: string, success: boolean, properties: EventProperties = {}) => {
      track(PostHogEvents.STORE_SCRAPED, {
        url,
        platform,
        success,
        ...properties,
      });
    },
    // THE consumer conversion: user clicks out to the merchant
    contactClicked: ({ platform, surface, ...props }: StoreEventProps & { platform: string; surface: string }) => {
      track(PostHogEvents.STORE_CONTACT_CLICKED, { platform, surface, ...props });
    },
    shared: ({ method, surface, ...props }: StoreEventProps & { method: 'native' | 'clipboard'; surface: string }) => {
      track(PostHogEvents.STORE_SHARED, { method, surface, ...props });
    },
    // Reader switches TO the excluded-reviews tab (never on re-clicking the active tab).
    flaggedReviewsTabViewed: ({ ...props }: StoreEventProps) => {
      track(PostHogEvents.STORE_FLAGGED_REVIEWS_TAB_VIEWED, { ...props });
    },
    submitted: (properties: EventProperties = {}) => {
      track(PostHogEvents.STORE_SUBMITTED, properties);
    },
    submissionCompleted: ({ success, ...props }: EventProperties & { success: boolean }) => {
      track(PostHogEvents.STORE_SUBMISSION_COMPLETED, { success, ...props });
    },
    // Submitted a URL we already have: user clicks through to the existing store.
    duplicateViewClicked: ({ store_id, ...props }: EventProperties & { store_id: string }) => {
      track(PostHogEvents.STORE_DUPLICATE_VIEW_CLICKED, { store_id, ...props });
    },
    submissionFormStepCompleted: ({ step_index, step_name, ...props }: EventProperties & { step_index: number; step_name: string }) => {
      track(PostHogEvents.SUBMISSION_FORM_STEP_COMPLETED, { step_index, step_name, ...props });
    },
    submissionFormSubmitted: (properties: EventProperties = {}) => {
      track(PostHogEvents.SUBMISSION_FORM_SUBMITTED, properties);
    },
  }), [track]);

  // Track review-related events
  const trackReview = useMemo(() => ({
    created: (storeId: string, rating: number, properties: EventProperties = {}) => {
      track(PostHogEvents.REVIEW_CREATED, {
        store_id: storeId,
        rating,
        ...properties,
      });
    },
    viewed: (reviewId: string, storeId: string, properties: EventProperties = {}) => {
      track(PostHogEvents.REVIEW_VIEWED, {
        review_id: reviewId,
        store_id: storeId,
        ...properties,
      });
    },
    liked: (reviewId: string, storeId: string, properties: EventProperties = {}) => {
      track(PostHogEvents.REVIEW_LIKED, {
        review_id: reviewId,
        store_id: storeId,
        ...properties,
      });
    },
    // Measures the auth gate on "write a review"
    ctaClicked: ({ surface, authenticated, ...props }: StoreEventProps & { surface: string; authenticated: boolean }) => {
      track(PostHogEvents.REVIEW_CTA_CLICKED, { surface, authenticated, ...props });
    },
    reported: (reviewId: string, reason: string, properties: EventProperties = {}) => {
      track(PostHogEvents.REVIEW_REPORTED, { review_id: reviewId, reason, ...properties });
    },
    commentAdded: (reviewId: string, storeId: string, properties: EventProperties = {}) => {
      track(PostHogEvents.REVIEW_COMMENT_ADDED, { review_id: reviewId, store_id: storeId, ...properties });
    },
    // The «عرض المصدر» action on an external review card. `external_source` is the platform
    // key (facebook_group, google, ...), never the source URL or the author name.
    sourceClicked: ({
      review_id,
      store_id,
      external_source,
      surface,
    }: {
      review_id: string;
      store_id: string;
      external_source: string;
      surface: string;
    }) => {
      track(PostHogEvents.REVIEW_SOURCE_CLICKED, { review_id, store_id, external_source, surface });
    },
    // The «مشاركة» action on a review card. Fires only once the share sheet or copy succeeded.
    shared: ({
      review_id,
      store_id,
      method,
      surface,
    }: {
      review_id: string;
      store_id: string;
      method: 'native' | 'clipboard';
      surface: string;
    }) => {
      track(PostHogEvents.REVIEW_SHARED, { review_id, store_id, method, surface });
    },
    // The cross-sell tap on the post-submit thank-you state. `rating` is the submitted score, which
    // is what makes "does a 1-star reviewer keep browsing?" answerable. No parameter here could
    // carry the review's text, the reviewer's name or their email: the signature is the enforcement.
    thankYouSearchClicked: ({ surface, rating, ...props }: StoreEventProps & { surface: string; rating: number }) => {
      track(PostHogEvents.REVIEW_THANKYOU_SEARCH_CLICKED, { surface, rating, ...props });
    },
    // The skipped-element upsell on the same state. `element` is one of text|photo|factors and
    // `points_delta` is what the element is worth, so "which delta actually recovers a photo?" is
    // answerable without ever carrying the text the user then wrote: same signature discipline as
    // `thankYouSearchClicked`, no parameter here can hold prose, a name or an email.
    thankYouUpsellClicked: ({
      surface,
      element,
      points_delta,
      rating,
      ...props
    }: StoreEventProps & {
      surface: string;
      element: 'text' | 'photo' | 'factors';
      points_delta: number;
      rating: number;
    }) => {
      track(PostHogEvents.REVIEW_THANKYOU_UPSELL_CLICKED, {
        surface,
        element,
        points_delta,
        rating,
        ...props,
      });
    },
    // The author tier chip on a review card. NO store props and no author NAME: the interesting
    // breakdown is which tier levels get followed, and `target_user_id` is an opaque id, not PII.
    tierChipClicked: ({
      surface,
      tier_level,
      target_user_id,
    }: {
      surface: string;
      tier_level: number;
      target_user_id: string;
    }) => {
      track(PostHogEvents.PROFILE_TIER_CHIP_CLICKED, { surface, tier_level, target_user_id });
    },
  }), [track]);

  // Public profile (/u/@id). NO PII: the profile owner's name and email belong in identify()
  // person properties, and `viewer_role` is the whole point of the event.
  const trackProfile = useMemo(() => ({
    viewed: ({
      viewer_role,
      tier_level,
    }: {
      viewer_role: 'visitor' | 'owner' | 'admin';
      tier_level: number;
    }) => {
      track(PostHogEvents.PUBLIC_PROFILE_VIEWED, { viewer_role, tier_level });
    },
    historyTabOpened: ({
      viewer_role,
      tier_level,
    }: {
      viewer_role: 'owner' | 'admin';
      tier_level: number;
    }) => {
      track(PostHogEvents.PROFILE_HISTORY_TAB_OPENED, { viewer_role, tier_level });
    },
  }), [track]);

  // Claim funnel + merchant activation (see POSTHOG-EVENT-MAP.md for props)
  const trackClaim = useMemo(() => ({
    started: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_STARTED, properties),
    storeSelected: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_STORE_SELECTED, properties),
    otpSent: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_OTP_SENT, properties),
    otpVerified: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_OTP_VERIFIED, properties),
    otpFallback: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_OTP_FALLBACK, properties),
    evidenceSubmitted: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_EVIDENCE_SUBMITTED, properties),
    approvedViewed: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_APPROVED_VIEWED, properties),
    bannerClicked: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_BANNER_CLICKED, properties),
    bannerDismissed: (properties: EventProperties = {}) => track(PostHogEvents.CLAIM_BANNER_DISMISSED, properties),
  }), [track]);

  const trackMerchant = useMemo(() => ({
    widgetSnippetCopied: (properties: EventProperties = {}) => track(PostHogEvents.WIDGET_SNIPPET_COPIED, properties),
    widgetCreated: (properties: EventProperties = {}) => track(PostHogEvents.WIDGET_CREATED, properties),
    rateLinkCopied: (properties: EventProperties = {}) => track(PostHogEvents.RATE_LINK_COPIED, properties),
    shopifyInstallClicked: (properties: EventProperties = {}) => track(PostHogEvents.SHOPIFY_INSTALL_CLICKED, properties),
    reviewReplied: (properties: EventProperties = {}) => track(PostHogEvents.MERCHANT_REVIEW_REPLIED, properties),
  }), [track]);

  /**
   * Public blacklist (/blacklist + /fake-reviews).
   *
   * TEXT NEVER LEAVES THE PAGE, and the signatures are the enforcement: there is no parameter on
   * any helper below that could carry the visitor's search query, a business name, a review body
   * or a contact address. `query_length` is a length. `page_id` is a public Facebook id.
   *
   * Components call these, never `posthog.capture()` directly — the same CTA appears on a card, in
   * the modal and in the request form's "already listed" path, so the capture belongs in one
   * shared helper with a `surface` prop rather than at three call sites.
   */
  const trackBlacklist = useMemo(() => ({
    searchSubmitted: ({ query_length, results_count, ...props }: EventProperties & { query_length: number; results_count: number }) => {
      track(PostHogEvents.BLACKLIST_SEARCH_SUBMITTED, { query_length, results_count, ...props });
    },
    categorySelected: ({ category_group, sensitive, ...props }: EventProperties & { category_group: string; sensitive: boolean }) => {
      track(PostHogEvents.BLACKLIST_CATEGORY_SELECTED, { category_group, sensitive, ...props });
    },
    // `direction` is GONE: the negative-campaign filter was removed from the public surface on
    // 2026-09-24 (unverifiable on 7 of 8 labelled pages, a false positive on the 1 we could
    // check). `sort` is now the only thing this row carries.
    filterApplied: ({ sort, ...props }: EventProperties & { sort: string }) => {
      track(PostHogEvents.BLACKLIST_FILTER_APPLIED, { sort, ...props });
    },
    // `surface` is REQUIRED. One CTA, three places (the grid, the pinned spotlight, the
    // autocomplete), and without it they collapse into one undifferentiated number — which is
    // exactly the question this event exists to answer. Instrumented once inside BlacklistTool's
    // shared `openModal`, never at the three call sites.
    pageModalOpened: ({ page_id, fake_review_count, category_group, surface, ...props }: EventProperties & { page_id: string; fake_review_count: number; category_group: string; surface: BlacklistModalSurface }) => {
      track(PostHogEvents.BLACKLIST_PAGE_MODAL_OPENED, { page_id, fake_review_count, category_group, surface, ...props });
    },
    // `surface` NARROWED to 'modal'. The card footer that carried `surface: "card"` was removed
    // when the whole card became one button (a `<button>` may not contain an `<a>`), so the value
    // is no longer reachable. The prop stays typed rather than deleted: the next surface that
    // needs an outbound Facebook link extends this union and the breakdown keeps working.
    fbReviewsTabClicked: ({ page_id, surface, ...props }: EventProperties & { page_id: string; surface: 'modal' }) => {
      track(PostHogEvents.BLACKLIST_FB_REVIEWS_TAB_CLICKED, { page_id, surface, ...props });
    },
    requestSubmitted: ({ already_listed, ...props }: EventProperties & { already_listed: boolean }) => {
      track(PostHogEvents.BLACKLIST_REQUEST_SUBMITTED, { already_listed, ...props });
    },
    storyCtaClicked: ({ surface, ...props }: EventProperties & { surface: string }) => {
      track(PostHogEvents.BLACKLIST_STORY_CTA_CLICKED, { surface, ...props });
    },
    // `query_length`, NEVER the query and NEVER the page name. The registry's PII rule for this
    // surface is absolute: no search string, no business name, no review body. `position` is the
    // 0-based row index, which is what makes "do people click the first result" answerable
    // without storing what they typed.
    suggestionClicked: ({ page_id, position, query_length, ...props }: EventProperties & { page_id: string; position: number; query_length: number }) => {
      track(PostHogEvents.BLACKLIST_SUGGESTION_CLICKED, { page_id, position, query_length, ...props });
    },
    listExtended: ({ pages_loaded, items_total, sort, category_group, ...props }: EventProperties & { pages_loaded: number; items_total: number; sort: string; category_group: string }) => {
      track(PostHogEvents.BLACKLIST_LIST_EXTENDED, { pages_loaded, items_total, sort, category_group, ...props });
    },
  }), [track]);

  /**
   * Blog (Phase 51, D-16). `linkClicked` is instrumented ONCE inside the shared link primitives
   * (BlogAnchor for in-post and index links, BlogTrustLink on store/category pages), never at call
   * sites, and `surface` is required so the four places a blog link lives stay apart in the
   * breakdown. Build its props with `blogLinkEventProps` (lib/blog/tracking.ts), which adds
   * storeEventProps on store targets and omits `post_slug` off the blog_post surface.
   */
  const trackBlog = useMemo(() => ({
    postViewed: ({ post_slug, cluster, ...props }: EventProperties & { post_slug: string; cluster: string }) => {
      track(PostHogEvents.BLOG_POST_VIEWED, { post_slug, cluster, ...props });
    },
    linkClicked: ({ surface, target_type, target_slug, ...props }: EventProperties & { surface: BlogLinkSurface; target_type: string; target_slug: string | null; post_slug?: string }) => {
      track(PostHogEvents.BLOG_LINK_CLICKED, { surface, target_type, target_slug, ...props });
    },
  }), [track]);

  // Track navigation events
  const trackNavigation = useMemo(() => ({
    categoryViewed: (categoryName: string, categorySlug: string, properties: EventProperties = {}) => {
      track(PostHogEvents.CATEGORY_VIEWED, {
        category_name: categoryName,
        category_slug: categorySlug,
        ...properties,
      });
    },
    // category_insight_clicked: a click from one of the category page's data blocks to a store
    // or another category.
    categoryInsightClicked: (
      categorySlug: string,
      block: 'recent_review' | 'most_reviewed' | 'related_category' | 'all_stores',
      targetSlug: string
    ) => {
      track(PostHogEvents.CATEGORY_INSIGHT_CLICKED, {
        category_slug: categorySlug,
        block,
        target_slug: targetSlug,
      });
    },
    // category_reviews_searched: a settled (debounced) query in the category page's search box.
    categoryReviewsSearched: (categorySlug: string, searchTerm: string, resultsCount: number) => {
      track(PostHogEvents.CATEGORY_REVIEWS_SEARCHED, {
        category_slug: categorySlug,
        search_term: searchTerm,
        results_count: resultsCount,
        zero_results: resultsCount === 0,
      });
    },
    searchPerformed: (searchTerm: string, resultsCount: number, properties: EventProperties = {}) => {
      track(PostHogEvents.SEARCH_PERFORMED, {
        search_term: searchTerm,
        results_count: resultsCount,
        zero_results: resultsCount === 0,
        ...properties,
      });
    },
    suggestionClicked: ({ query, type, position, ...props }: EventProperties & { query: string; type: 'store' | 'category'; position: number }) => {
      track(PostHogEvents.SEARCH_SUGGESTION_CLICKED, { query, type, position, ...props });
    },
    chatChannelClicked: ({ channel, surface, ...props }: EventProperties & { channel: string; surface: string }) => {
      track(PostHogEvents.CHAT_CHANNEL_CLICKED, { channel, surface, ...props });
    },
  }), [track]);

  // Track web chat panel events (Phase 38).
  //
  // TEXT NEVER LEAVES THE PANEL. Every helper below takes counts, ids, slugs and booleans, and the
  // signatures are the enforcement: there is no parameter on any of them that could carry a message
  // body, a chip's Arabic label, or a partial answer. `partial_chars` is a length, `frames_replayed`
  // is a count, `chip_id` is a stable key from `suggestions.ts`.
  //
  // All of these are called from ChatContext's own handlers, never from a component — the panel's
  // CTAs appear in several places (empty-state chips, composer, system rows) and the capture belongs
  // in the shared handler, per CLAUDE.md's shared-primitive rule.
  const trackChat = useMemo(() => ({
    panelOpened: ({ surface, unread, ...props }: EventProperties & { surface: string; unread: boolean }) => {
      track(PostHogEvents.CHAT_PANEL_OPENED, { surface, unread, ...props });
    },
    panelClosed: ({ messages_in_thread, ...props }: EventProperties & { messages_in_thread: number }) => {
      track(PostHogEvents.CHAT_PANEL_CLOSED, { messages_in_thread, ...props });
    },
    // `chip_id` and NEVER the chip text: the labels are Arabic prose that would arrive in PostHog as
    // free-form user-facing content, and the id is a stable join key that survives a copy edit.
    //
    // ONE EVENT NAME FOR BOTH CHIP KINDS (Phase 39). A model-suggested chip under an answer and an
    // empty-state chip are the same funnel step — the visitor took an offered prompt instead of
    // composing one — so they share this event and are told apart by `chip_id`. Minting a second
    // name would have made every existing `chat_suggestion_clicked` query quietly wrong by
    // excluding half the behaviour it was written to measure. `position` is which chip of the row
    // was taken; it is present on model chips, which have no stable id and use a fixed synthetic
    // one, and absent on the template chips, whose ids are already distinct.
    suggestionClicked: ({ chip_id, route_type, position, ...props }: EventProperties & { chip_id: string; route_type: string; position?: number }) => {
      track(PostHogEvents.CHAT_SUGGESTION_CLICKED, { chip_id, route_type, position, ...props });
    },
    // Deliberately NOT the full store-scoped prop set CLAUDE.md defines (`store_id`,
    // `store_verified`, `store_status`). This is intentional and must not be "fixed": D-13 scopes the
    // chat's page context to route type plus slugs, and the panel mounts in BrowserWidgets at the
    // layout level, outside any store page's data boundary — it holds no store document at all.
    // Populating the rest would mean fetching a store record per send purely to decorate an event.
    // The slugs are join keys; everything else about that store is already on its own store_* events
    // for the same session. Also never the message text.
    messageSent: ({ route_type, store_slug, category_slug, authenticated, ...props }: EventProperties & {
      route_type: string;
      store_slug?: string;
      category_slug?: string;
      authenticated: boolean;
    }) => {
      track(PostHogEvents.CHAT_MESSAGE_SENT, { route_type, store_slug, category_slug, authenticated, ...props });
    },
    // The real streaming-health metric: time to the FIRST delta. A buffering intermediary between the
    // browser and the chat dyno shows up here as a ttfb equal to the whole answer's duration.
    responseStreamed: ({ ttfb_ms, ...props }: EventProperties & { ttfb_ms: number }) => {
      track(PostHogEvents.CHAT_RESPONSE_STREAMED, { ttfb_ms, ...props });
    },
    generationStopped: ({ partial_chars, ...props }: EventProperties & { partial_chars: number }) => {
      track(PostHogEvents.CHAT_GENERATION_STOPPED, { partial_chars, ...props });
    },
    limitReached: ({ layer, authenticated, ...props }: EventProperties & { layer: string; authenticated: boolean }) => {
      track(PostHogEvents.CHAT_LIMIT_REACHED, { layer, authenticated, ...props });
    },
    loginNudgeClicked: ({ layer, ...props }: EventProperties & { layer: string }) => {
      track(PostHogEvents.CHAT_LOGIN_NUDGE_CLICKED, { layer, ...props });
    },
    streamReconnected: ({ frames_replayed, ...props }: EventProperties & { frames_replayed: number }) => {
      track(PostHogEvents.CHAT_STREAM_RECONNECTED, { frames_replayed, ...props });
    },
    // ─── Store carousel (Phase 39, WEB-03) ───
    //
    // The SAME deliberate exception recorded above for `messageSent` applies to both of these, and
    // for the same reason: they carry `store_slug` but NOT the full store-scoped set CLAUDE.md
    // defines (`store_id`, `store_verified`, `store_status`). The panel mounts in `BrowserWidgets`
    // at the layout level, outside any store page's data boundary, and holds no store document —
    // populating the rest would mean fetching a store record per card tap purely to decorate an
    // event. The slug is a join key, and everything else about that store is already on its own
    // `store_*` events for the same session.
    //
    // And, as everywhere else in this memo: no parameter below could carry prose. `store_slug` is a
    // URL segment, `position` and `card_count` are counts, `route_type` is one of three literals.
    // The card's Arabic title is never sent.
    storeCardClicked: ({ store_slug, position, card_count, route_type, ...props }: EventProperties & {
      store_slug: string;
      position: number;
      card_count: number;
      route_type: string;
    }) => {
      track(PostHogEvents.CHAT_STORE_CARD_CLICKED, { store_slug, position, card_count, route_type, ...props });
    },
    storeCarouselShown: ({ card_count, route_type, ...props }: EventProperties & { card_count: number; route_type: string }) => {
      track(PostHogEvents.CHAT_STORE_CAROUSEL_SHOWN, { card_count, route_type, ...props });
    },
    // The contact icon row (39-08). `contact_kind` is a closed literal set; the tapped
    // destination (a phone number, a wa.me URL, a maps query carrying the store name) is
    // deliberately NOT a parameter, for the same no-prose/no-PII rule as every helper above.
    storeContactClicked: ({ store_slug, contact_kind, position, route_type, ...props }: EventProperties & {
      store_slug: string;
      contact_kind: 'phone' | 'whatsapp' | 'maps';
      position: number;
      route_type: string;
    }) => {
      track(PostHogEvents.CHAT_STORE_CONTACT_CLICKED, { store_slug, contact_kind, position, route_type, ...props });
    },
  }), [track]);

  // Notifications (Phase 46). The signatures are the PII enforcement: no parameter can carry a
  // name, email, phone or the notification text. Never called from admin or moderator surfaces.
  const trackNotifications = useMemo(() => ({
    clicked: ({ type, surface }: { type: NotificationType; surface: NotificationClickSurface }) => {
      track(PostHogEvents.NOTIFICATION_CLICKED, { type, surface });
    },
    // One event per row toggle; `types` is the row's type list, joined so the value stays flat.
    prefsChanged: ({ types, channel, enabled }: {
      types: NotificationType[];
      channel: NotificationDeliveryChannel;
      enabled: boolean;
    }) => {
      track(PostHogEvents.NOTIFICATION_PREFS_CHANGED, { types: types.join(','), channel, enabled });
    },
    pushPermissionGranted: ({ surface }: { surface: PushPermissionSurface }) => {
      track(PostHogEvents.PUSH_PERMISSION_GRANTED, { surface });
    },
    whatsappOptinChanged: ({ enabled, source }: { enabled: boolean; source: WhatsappOptinSource }) => {
      track(PostHogEvents.WHATSAPP_OPTIN_CHANGED, { enabled, source });
    },
  }), [track]);

  // New-user onboarding (Phase 49). The signatures are the PII enforcement: triggers, counts,
  // booleans and storeEventProps only. Captured inside the shared components, never at call sites.
  const trackOnboarding = useMemo(() => ({
    welcomeCardShown: ({ trigger, itemsTotal, itemsDone }: {
      trigger: SignupTrigger;
      itemsTotal: number;
      itemsDone: number;
    }) => {
      track(PostHogEvents.WELCOME_CARD_SHOWN, { trigger, items_total: itemsTotal, items_done: itemsDone });
    },
    welcomeCardItemClicked: ({ item, trigger }: { item: WelcomeCardItem; trigger: SignupTrigger }) => {
      track(PostHogEvents.WELCOME_CARD_ITEM_CLICKED, { item, trigger });
    },
    welcomeCardDismissed: ({ trigger, itemsDone }: { trigger: SignupTrigger; itemsDone: number }) => {
      track(PostHogEvents.WELCOME_CARD_DISMISSED, { trigger, items_done: itemsDone });
    },
    signupFinishSubmitted: ({ whatsappOptin, whatsappAlreadyOn }: {
      whatsappOptin: boolean;
      whatsappAlreadyOn: boolean;
    }) => {
      track(PostHogEvents.SIGNUP_FINISH_SUBMITTED, {
        whatsapp_optin: whatsappOptin,
        whatsapp_already_on: whatsappAlreadyOn,
      });
    },
    returnBannerShown: ({ store }: { store: TrackableStore }) => {
      track(PostHogEvents.RETURN_BANNER_SHOWN, { ...storeEventProps(store), type: 'onboarding_day3' });
    },
    returnBannerReviewClicked: ({ store }: { store: TrackableStore }) => {
      track(PostHogEvents.RETURN_BANNER_REVIEW_CLICKED, { ...storeEventProps(store) });
    },
    returnBannerDismissed: ({ store }: { store: TrackableStore }) => {
      track(PostHogEvents.RETURN_BANNER_DISMISSED, { ...storeEventProps(store) });
    },
  }), [track]);

  // Track gamification events
  const trackGamification = useMemo(() => ({
    pointsEarned: (points: number, action: string, properties: EventProperties = {}) => {
      track(PostHogEvents.POINTS_EARNED, {
        points,
        action,
        ...properties,
      });
    },
    levelUp: (newLevel: number, totalPoints: number, properties: EventProperties = {}) => {
      track(PostHogEvents.LEVEL_UP, {
        new_level: newLevel,
        total_points: totalPoints,
        ...properties,
      });
    },
  }), [track]);

  // Track errors
  const trackError = useCallback((error: string, context: string, properties: EventProperties = {}) => {
    track(PostHogEvents.ERROR_OCCURRED, {
      error,
      context,
      timestamp: new Date().toISOString(),
      ...properties,
    });
  }, [track]);

  const trackApiError = useCallback((endpoint: string, status: number, error: string, properties: EventProperties = {}) => {
    track(PostHogEvents.API_ERROR, {
      endpoint,
      status,
      error,
      timestamp: new Date().toISOString(),
      ...properties,
    });
  }, [track]);

  return {
    posthog,
    trackPage,
    track,
    identify,
    reset,
    trackAuth,
    trackStore,
    trackReview,
    trackProfile,
    trackClaim,
    trackMerchant,
    trackNavigation,
    trackBlacklist,
    trackBlog,
    trackChat,
    trackNotifications,
    trackOnboarding,
    trackGamification,
    trackError,
    trackApiError,
  };
};

// Hook for automatically tracking page views
export const usePageTracking = (pageName: string, properties: EventProperties = {}) => {
  const { trackPage } = usePostHogTracking();

  useEffect(() => {
    trackPage(pageName, properties);
  }, [pageName, properties, trackPage]);
};

export default usePostHogTracking;